import { useState, useEffect, useRef } from "react";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  onSnapshot,
  query,
  orderBy,
} from "firebase/firestore";
import LoginSignupPage from "./LoginSignupPage";
import WorldContextPanel from "./components/WorldContextPanel";
import { buildPrompt } from "./utils/buildPrompt";
import DiscoverMaterialButton from "./components/DiscoverMaterialButton";
import DiscoveredMaterialsViewer from "./components/DiscoveredMaterialsViewer";
function App() {
  const [user, setUser] = useState(null);
  const [isDM, setIsDM] = useState(false);
  const [profile, setProfile] = useState(null);
  const [allProfiles, setAllProfiles] = useState([]);
  const [view, setView] = useState("profile");
  const [craftingVisible, setCraftingVisible] = useState(true);
  const [log, setLog] = useState([]);
  const calculateModifier = (score) => {
    if (score <= 1) return -5;
    if (score <= 3) return -4;
    if (score <= 5) return -3;
    if (score <= 7) return -2;
    if (score <= 9) return -1;
    if (score <= 11) return 0;
    if (score <= 13) return 1;
    if (score <= 15) return 2;
    if (score <= 17) return 3;
    if (score <= 19) return 4;
    return 5;
  };
  const [selectedProfile, setSelectedProfile] = useState(null);
  const calculateMod = (score) => {
    if (score === undefined || score === null) return 0;
    return Math.floor((score - 10) / 2);
  };
  const ABILITY_ORDER = ["STR", "DEX", "CON", "INT", "WIS", "CHA"];
  const SKILL_ORDER = [
    "Agility",
    "Athletics",
    "Crafting",
    "Discovery",
    "Perception",
    "Persuasion",
    "Survival",
  ];
  const RARITIES = ["Common", "Uncommon", "Rare", "Legendary", "Celestial"];
  const QUALITIES = ["Poor", "Good", "Reliable", "Finest", "Impeccable"];
  const toTitleCase = (str) =>
    str.replace(
      /\w\S*/g,
      (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
    );
  const [isEditing, setIsEditing] = useState(false);
  const editTimeoutRef = useRef(null);
  const debounceDMUpdate = (updatedInventory) => {
    // Update UI immediately so the DM sees changes right away
    setSelectedProfile((prev) => ({ ...prev, inventory: updatedInventory }));

    // Cancel any previous scheduled save to Firebase
    if (editTimeoutRef.current) clearTimeout(editTimeoutRef.current);

    // Schedule a new save to Firebase after 1 second
    editTimeoutRef.current = setTimeout(() => {
      updateDoc(doc(db, "profiles", selectedProfile.id), {
        inventory: updatedInventory,
      });
    }, 1000); // Delay in milliseconds (1000ms = 1s)
  };
  const [showMaterials, setShowMaterials] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);

  const [profileDraft, setProfileDraft] = useState(null);
  const debounceProfileUpdate = (field, value) => {
    setProfileDraft((prev) => ({ ...prev, [field]: value }));
    if (editTimeoutRef.current) clearTimeout(editTimeoutRef.current);
    editTimeoutRef.current = setTimeout(() => {
      handleProfileUpdate(field, value);
    }, 1000); // Debounced save
  };
  // Profile inventory modal
  const [showModal, setShowModal] = useState(false);
  const [newItem, setNewItem] = useState({
    name: "",
    quality: "Poor",
    rarity: "Common",
    qty: 1,
    tags: [],
    description: "",
    newTag: "",
  });
  const [showDMModal, setShowDMModal] = useState(false);
  const [dmNewItem, setDMNewItem] = useState({
    name: "",
    quality: "Poor",
    rarity: "Common",
    qty: 1,
    tags: [],
    description: "",
    newTag: "",
  });
  const [currentLocation, setCurrentLocation] = useState("");
  const [currentEvent, setCurrentEvent] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [adventureLogs, setAdventureLogs] = useState([]);

  // Crafting fields
  const [itemName, setItemName] = useState("");
  const [complexity, setComplexity] = useState("Simple");
  const [materials, setMaterials] = useState([]);
  const [hasTool, setHasTool] = useState(false);
  const [toolQuality, setToolQuality] = useState("Poor");
  const [constitutionMod, setConstitutionMod] = useState(0);
  const [intelligenceMod, setIntelligenceMod] = useState(0);
  const [finalResult, setFinalResult] = useState("");
  const [debugLog, setDebugLog] = useState([]);
  useEffect(() => {
    const q = query(
      collection(db, "adventureLogs"),
      orderBy("createdAt", "desc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const loadedLogs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAdventureLogs(loadedLogs);
    });

    return () => unsubscribe();
  }, []);

  const tabStyle = (name) => ({
    marginRight: 8,
    padding: "6px 12px",
    border: view === name ? "2px solid black" : "1px solid #ccc",
    background: view === name ? "#f5f5f5" : "white",
    fontWeight: view === name ? "bold" : "normal",
    cursor: "pointer",
  });
  useEffect(() => {
    const globalSettingsRef = doc(db, "settings", "global");
    const unsubscribeGlobalSettings = onSnapshot(globalSettingsRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (typeof data.craftingVisible === "boolean") {
          setCraftingVisible(data.craftingVisible);

          // 🛑 Auto-kick player off crafting if DM hides crafting
          if (!data.craftingVisible && !isDM && view === "crafting") {
            setView("profile");
          }
        }
      }
    });

    return () => unsubscribeGlobalSettings();
  }, [isDM, view]);
  useEffect(() => {
    if (profile && typeof profile.hp === "number") {
      handleProfileUpdate("hp", { current: profile.hp, max: profile.hp });
    }
  }, [profile]);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const profileRef = doc(db, "profiles", currentUser.uid);

        // 👇 Listen to real-time changes
        const unsubscribeProfile = onSnapshot(profileRef, async (snap) => {
          if (snap.exists()) {
            const data = snap.data();

            const defaultFields = {
              name: "New Player",
              isDM: false,
              hp: { current: 10, max: 10 },
              ac: 10,
              speed: 30,
              bodyTemp: 50,
              hunger: 50,
              abilityScores: {
                STR: 10,
                DEX: 10,
                CON: 10,
                INT: 10,
                WIS: 10,
                CHA: 10,
              },
              skills: {
                Agility: 0,
                Athletics: 0,
                Crafting: 0,
                Discovery: 0,
                Perception: 0,
                Persuasion: 0,
                Survival: 0,
              },
              inventory: [],
            };

            const completeData = {
              ...defaultFields,
              ...data,
              abilityScores: {
                ...defaultFields.abilityScores,
                ...data.abilityScores,
              },
              skills: { ...defaultFields.skills, ...data.skills },
            };

            // Patch Firestore if any defaults were added
            await setDoc(profileRef, completeData, { merge: true });

            if (!isEditing) {
              setProfile(completeData);
              setProfileDraft(completeData);
            }

            // Always update craftingVisible, even if editing
            if (typeof completeData.craftingVisible === "boolean") {
              setCraftingVisible(completeData.craftingVisible);

              // 🛑 Auto-kick player off crafting view if DM hides crafting
              if (
                !completeData.craftingVisible &&
                !isDM &&
                view === "crafting"
              ) {
                setView("profile");
              }
            }

            setIsDM(completeData.isDM || false);
          }
        });

        // Return unsubscribe function
        return () => unsubscribeProfile();
      } else {
        setUser(null);
        setProfile(null);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (view === "dm" && isDM) {
      const fetchProfiles = async () => {
        const snapshot = await getDocs(collection(db, "profiles"));
        const profiles = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAllProfiles(profiles);
      };
      fetchProfiles();
    }
  }, [view, isDM]);

  useEffect(() => {
    if (!selectedProfile?.id) return;

    const ref = doc(db, "profiles", selectedProfile.id);
    const unsubscribe = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        setSelectedProfile({ id: snap.id, ...snap.data() });
      }
    });

    return () => unsubscribe();
  }, [selectedProfile?.id]);

  let editTimeout;

  const handleProfileUpdate = async (field, value) => {
    if (!user) return;

    setIsEditing(true);
    const updated = { ...profileDraft, [field]: value };
    setProfileDraft(updated);
    setProfile(updated); // Keeps Firestore and draft aligned

    // Clear any existing timer
    if (editTimeoutRef.current) {
      clearTimeout(editTimeoutRef.current);
    }

    // Set a new one
    editTimeoutRef.current = setTimeout(async () => {
      const ref = doc(db, "profiles", user.uid);
      await updateDoc(ref, { [field]: value });
      setIsEditing(false);
    }, 1000); // 400ms debounce
  };

  const updateAbility = async (k, v) => {
    const updated = { ...profile.abilityScores, [k]: v };
    await handleProfileUpdate("abilityScores", updated);
  };

  const updateSkill = async (k, v) => {
    const updated = { ...profile.skills, [k]: v };
    await handleProfileUpdate("skills", updated);
  };

  const removeInventoryItem = async (i) => {
    const inv = [...profileDraft.inventory];
    inv.splice(i, 1);
    await handleProfileUpdate("inventory", inv);
  };

  const getKnowledgeBonus = (k) =>
    ({
      None: -6,
      Basic: -2,
      Novice: 0,
      "Well Read": 1,
      Expert: 2,
      Master: 4,
    }[k] || 0);

  const getSubBonus = (n) => {
    if (n < 15) return -6;
    if (n < 20) return -1;
    if (n < 30) return 0;
    if (n < 38) return 1;
    return 2;
  };

  const getResultLabel = (score) => {
    if (score < 0.5) return "Crafting failed! You lose all materials.";
    if (score < 2.2) return "Common";
    if (score < 3.2) return "Sturdy";
    if (score < 3.6) return "Rare";
    if (score < 4) return "Legendary";
    return "Celestial";
  };

  const mapResultToRarity = (resultLabel) => {
    switch (resultLabel) {
      case "Common":
        return "Common";
      case "Sturdy":
        return "Uncommon";
      case "Rare":
        return "Rare";
      case "Legendary":
        return "Legendary";
      case "Celestial":
        return "Celestial";
      default:
        return "Common";
    }
  };

  const handleCraft = async () => {
    if (!itemName || materials.length === 0) return;

    let debugLines = [];
    let total = 0;
    let containsLegendary = false;

    const toolBonus =
      {
        Poor: 0,
        Good: 0.5,
        Reliable: 1,
        Finest: 1.5,
        Impeccable: 2,
      }[toolQuality] || 0;

    // Step 1: Loop through materials and calculate scores
    materials.forEach((m, i) => {
      for (let count = 0; count < m.qty; count++) {
        const roll1 = Math.floor(Math.random() * 20) + 1;
        const roll2 = Math.floor(Math.random() * 20) + 1;

        const conTotal =
          calculateModifier(profile?.abilityScores?.CON ?? 10) + roll1;
        const intTotal =
          calculateModifier(profile?.abilityScores?.INT ?? 10) + roll2;

        const knowledgeBonus = ["Legendary", "Celestial"].includes(m.rarity)
          ? getKnowledgeBonus(m.knowledge)
          : 0;

        const craftingSubBonus = conTotal + intTotal + knowledgeBonus;
        const subMod = getSubBonus(craftingSubBonus);

        const qualityScore = {
          Poor: 0,
          Good: 0.5,
          Reliable: 1,
          Finest: 1.5,
          Impeccable: 2,
        }[m.quality];

        const rarityScore = {
          Common: 1,
          Uncommon: 1.5,
          Rare: 2,
          Legendary: 2.5,
          Celestial: 3,
        }[m.rarity];

        const materialScore = qualityScore + rarityScore + toolBonus + subMod;
        total += Math.min(materialScore, 19);

        debugLines.push(
          `🔹 Material ${m.name} (Roll ${count + 1} of ${m.qty}): (${
            m.quality
          }, ${m.rarity})`,
          `   Rolls → CON(${calculateModifier(
            profile.abilityScores.Constitution
          )} + ${roll1}) + INT(${calculateModifier(
            profile.abilityScores.Intelligence
          )} + ${roll2}) + Knowledge(${knowledgeBonus}) = ${craftingSubBonus}`,
          `   → Modifier: ${subMod}`,
          `   → Total Score for this material: ${Math.min(materialScore, 19)}`,
          ``
        );
      }
    });

    const average = total / materials.length;
    const complexityScore =
      {
        Simple: 0,
        Focused: -2,
        Complex: -4,
        Engineered: -6,
        Unimaginable: -8,
      }[complexity] || 0;

    const rawScore = average + complexityScore + toolBonus;
    const finalScore = rawScore / (containsLegendary ? 2.5 : 2);

    const getQualityLabel = (score) => {
      if (score < 15) return "Poor";
      if (score < 20) return "Good";
      if (score < 25) return "Reliable";
      if (score < 30) return "Finest";
      return "Impeccable";
    };

    const result = getResultLabel(finalScore);
    const quality = getQualityLabel(rawScore);
    const rarity = mapResultToRarity(result);

    // Step 2: Append final results to log
    debugLines.push(
      `🧰 Tool Quality: ${toolQuality} (+${toolBonus})`,
      `🧠 Complexity: ${complexity} (${complexityScore})`,
      `🎯 Raw Score: ${rawScore.toFixed(2)}`,
      `📏 Final Score: ${finalScore.toFixed(2)} ${
        containsLegendary ? "(÷2)" : "(÷1.5)"
      }`,
      `🏆 Result: ${result}`,
      `📦 Inventory Add → ${itemName} (Quality: ${quality}, Rarity: ${rarity})`
    );

    const newInventory = [...profile.inventory];

    // Step 3: Remove used materials from inventory
    for (const mat of materials) {
      const idx = newInventory.findIndex(
        (i) =>
          i.name === mat.name &&
          i.quality === mat.quality &&
          i.rarity === mat.rarity
      );
      if (idx !== -1) {
        newInventory[idx].qty -= mat.qty;
        if (newInventory[idx].qty <= 0) {
          newInventory.splice(idx, 1);
        }
      }
    }

    // Step 4: Add crafted item to inventory if successful
    if (!result.includes("failed")) {
      const match = newInventory.find(
        (i) =>
          i.name === itemName && i.quality === quality && i.rarity === rarity
      );
      if (match) {
        match.qty += 1;
      } else {
        newInventory.push({
          name: toTitleCase(itemName),
          quality,
          rarity,
          qty: 1,
        });
      }
    }

    await handleProfileUpdate("inventory", newInventory);
    setFinalResult(
      result.includes("failed")
        ? result
        : `🎉 You crafted a ${result} ${toTitleCase(itemName)}!`
    );
    setLog(debugLines);
    setMaterials([]);
    setItemName("");
  };

  if (user && !profile) {
    return <div style={{ padding: 20 }}>Loading profile...</div>;
  }

  return (
    <div
      style={{
        fontFamily: "sans-serif",
        padding: 10,
        maxWidth: "100%",
        width: "100vw",
        boxSizing: "border-box",
      }}
    >
      <style>{`
    .modal-button {
      flex: 1;
      background: green;
      color: white;
      padding: 10px;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.2s ease;
      text-align: center;
    }

    .modal-button:hover {
      filter: brightness(90%);
    }

    .modal-button:active {
      transform: scale(0.95);
      filter: brightness(80%);
    }

    .modal-button.cancel {
      background: gray;
    }

    .modal-button.delete {
      background: red;
    }

    .modal-button.duplicate {
      background: blue;
    }
  `}</style>
      {!user ? (
        <LoginSignupPage onUserAuthenticated={setUser} />
      ) : (
        <div>
          <button
            onClick={() => {
              auth.signOut().then(() => {
                setUser(null);
                setProfile(null);
                setIsDM(false);
                setView("profile");
                window.location.reload();
              });
            }}
            style={{ position: "absolute", top: 10, right: 10 }}
          >
            Log Out
          </button>
          <h2>Echoes of the Wild App</h2>

          {isDM && (
            <>
              <p style={{ color: "green" }}>You are logged in as DM.</p>
              <label>
                <input
                  type="checkbox"
                  checked={craftingVisible}
                  onChange={async (e) => {
                    const checked = e.target.checked;
                    setCraftingVisible(checked); // ✅ Update local

                    const globalSettingsRef = doc(db, "settings", "global");
                    await setDoc(
                      globalSettingsRef,
                      { craftingVisible: checked },
                      { merge: true }
                    );
                  }}
                />{" "}
                Show Crafting Tab to Players
              </label>
            </>
          )}

          <div style={{ marginBottom: "1rem", marginTop: 12 }}>
            <button
              style={tabStyle("profile")}
              onClick={() => setView("profile")}
            >
              Profile
            </button>
            {(isDM || craftingVisible) && (
              <button
                style={tabStyle("crafting")}
                onClick={() => setView("crafting")}
              >
                Crafting
              </button>
            )}
            {isDM && (
              <button style={tabStyle("dm")} onClick={() => setView("dm")}>
                DM Dashboard
              </button>
            )}
          </div>

          {view === "profile" && profileDraft && (
            <div
              style={{
                maxWidth: 700,
                padding: "1rem",
                background: "#f9f9f9",
                borderRadius: 8,
              }}
            >
              <div>
                <h3>
                  Character Name:{" "}
                  <input
                    value={profileDraft.name}
                    onChange={(e) =>
                      handleProfileUpdate("name", e.target.value)
                    }
                  />
                </h3>
                <p>
                  HP:{" "}
                  <input
                    value={profileDraft.hp?.current ?? 0}
                    onChange={(e) => {
                      const newCurrent = parseInt(e.target.value);
                      const clamped = Math.max(
                        0,
                        Math.min(newCurrent, profileDraft.hp?.max ?? 10)
                      );
                      handleProfileUpdate("hp", {
                        ...profileDraft.hp,
                        current: clamped,
                      });
                    }}
                  />{" "}
                  /{" "}
                  <input
                    value={profileDraft.hp?.max ?? 0}
                    onChange={(e) =>
                      handleProfileUpdate("hp", {
                        ...profileDraft.hp,
                        max: parseInt(e.target.value),
                      })
                    }
                  />
                </p>
                <p>
                  AC:{" "}
                  <input
                    value={profileDraft.ac ?? 10}
                    onChange={(e) =>
                      handleProfileUpdate("ac", parseInt(e.target.value))
                    }
                  />
                </p>
                <p>
                  Speed:{" "}
                  <input
                    value={profileDraft.speed ?? 30}
                    onChange={(e) =>
                      handleProfileUpdate("speed", parseInt(e.target.value))
                    }
                  />{" "}
                  ft
                </p>
              </div>
              {/* 🧠 Here is where <h4>Condition</h4> goes */}
              <h3>Condition:</h3>
              <div style={{ marginBottom: "1rem" }}>
                Body Temperature:
                <div
                  style={{
                    background: "#ddd",
                    height: "20px",
                    width: "300px",
                    borderRadius: "10px",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      background: "#4aa1f3",
                      height: "100%",
                      width: `${profile.bodyTemp ?? 50}%`,
                      transition: "width 0.3s",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: "50%",
                      width: "2px",
                      height: "100%",
                      background: "#444",
                    }}
                  />
                </div>
                <small>{profile.bodyTemp ?? 50}% — Stay centered!</small>
              </div>
              <div style={{ marginBottom: "1.5rem" }}>
                Hunger:
                <div
                  style={{
                    background: "#ddd",
                    height: "20px",
                    width: "300px",
                    borderRadius: "10px",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      background: "#f3a14a",
                      height: "100%",
                      width: `${profile.hunger ?? 50}%`,
                      transition: "width 0.3s",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: "50%",
                      width: "2px",
                      height: "100%",
                      background: "#444",
                    }}
                  />
                </div>
                <small>{profile.hunger ?? 50}% — Stay centered!</small>
              </div>
              <h3>Ability Scores:</h3>
              {profile?.abilityScores ? (
                ABILITY_ORDER.map((key) => {
                  const val = profile.abilityScores[key] ?? 0;
                  const mod = Math.floor((val - 10) / 2);
                  return (
                    <p key={key}>
                      {key}:{" "}
                      <input
                        type="number"
                        value={val}
                        onChange={(e) => {
                          const score = parseInt(e.target.value);
                          const updated = {
                            ...profile.abilityScores,
                            [key]: score,
                          };
                          handleProfileUpdate("abilityScores", updated);
                        }}
                        style={{ width: "100%" }}
                      />
                      <span
                        style={{
                          marginLeft: 10,
                          color: "#555",
                          fontSize: "0.85rem",
                        }}
                      >
                        (Mod: {mod >= 0 ? "+" : ""}
                        {mod})
                      </span>
                    </p>
                  );
                })
              ) : (
                <p>Loading ability scores...</p>
              )}
              <h3>Skills:</h3>
              {profile?.skills ? (
                SKILL_ORDER.map((skill) => {
                  const value = profile.skills[skill] ?? 0;
                  return (
                    <div key={skill}>
                      {skill}:{" "}
                      {[1, 2, 3, 4, 5].map((rank) => (
                        <input
                          key={rank}
                          type="checkbox"
                          checked={value >= rank}
                          onChange={() => {
                            const newRank = rank === value ? rank - 1 : rank;
                            const updated = {
                              ...profile.skills,
                              [skill]: newRank,
                            };
                            handleProfileUpdate("skills", updated);
                          }}
                          style={{ marginRight: 6 }}
                        />
                      ))}
                    </div>
                  );
                })
              ) : (
                <p>Loading skills...</p>
              )}
              <h3>Inventory:</h3>
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                {profileDraft?.inventory?.length > 0 && (
                  <div style={{ marginBottom: "8px" }}>
                    <select
                      value={tagFilter}
                      onChange={(e) => setTagFilter(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "6px",
                        borderRadius: "6px",
                        border: "1px solid #ccc",
                      }}
                    >
                      <option value="">Show All Items</option>
                      {[
                        ...new Set(
                          profileDraft.inventory.flatMap(
                            (item) => item.tags || []
                          )
                        ),
                      ]
                        .filter((tag) => tag) // remove empty tags
                        .sort()
                        .map((tag, idx) => (
                          <option key={idx} value={tag}>
                            {tag}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                {(profileDraft?.inventory || [])
                  .filter((item) =>
                    !tagFilter.trim()
                      ? true
                      : (item.tags || []).includes(
                          tagFilter.trim().toLowerCase()
                        )
                  )
                  .map((item, index) => (
                    <div
                      key={index}
                      onClick={() => {
                        setSelectedItem({ ...item, index });
                        setShowItemModal(true);
                      }}
                      style={{
                        padding: "10px",
                        background: "#f9f9f9",
                        borderRadius: "6px",
                        cursor: "pointer",
                        border: "1px solid #ccc",
                      }}
                    >
                      <strong>{item.name}</strong> — {item.quality},{" "}
                      {item.rarity} — Qty: {item.qty}
                      <div style={{ fontSize: "0.85rem", marginTop: 4 }}>
                        Tags: {item.tags?.join(", ") || "None"}
                      </div>
                    </div>
                  ))}
              </div>

              <button
                onClick={() => setShowModal(true)}
                style={{ marginTop: 10 }}
              >
                ➕ Add New Item
              </button>

              {showModal && (
                <div
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background: "rgba(0,0,0,0.5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1000,
                  }}
                >
                  <div
                    style={{
                      background: "white",
                      padding: 20,
                      borderRadius: 8,
                      width: "90%",
                      maxWidth: 300,
                      maxHeight: "90vh",
                      overflowY: "auto",
                    }}
                  >
                    <h3>Add New Item</h3>

                    {/* 🟢 Item Name at the TOP */}
                    <input
                      value={newItem.name}
                      onChange={(e) =>
                        setNewItem({ ...newItem, name: e.target.value })
                      }
                      placeholder="Item name"
                      style={{ width: "100%", marginBottom: 10 }}
                    />

                    <select
                      value={newItem.quality}
                      onChange={(e) =>
                        setNewItem({ ...newItem, quality: e.target.value })
                      }
                    >
                      <option>Poor</option>
                      <option>Good</option>
                      <option>Reliable</option>
                      <option>Finest</option>
                      <option>Impeccable</option>
                    </select>

                    <select
                      value={newItem.rarity}
                      onChange={(e) =>
                        setNewItem({ ...newItem, rarity: e.target.value })
                      }
                    >
                      <option>Common</option>
                      <option>Uncommon</option>
                      <option>Rare</option>
                      <option>Legendary</option>
                      <option>Celestial</option>
                    </select>

                    <input
                      type="number"
                      min={1}
                      value={newItem.qty}
                      onChange={(e) =>
                        setNewItem({
                          ...newItem,
                          qty: parseInt(e.target.value),
                        })
                      }
                    />

                    {/* 📖 Description field */}
                    <input
                      value={newItem.description}
                      onChange={(e) =>
                        setNewItem({ ...newItem, description: e.target.value })
                      }
                      placeholder="Description"
                      style={{ width: "100%", marginTop: 10 }}
                    />

                    {/* 🏷️ New TAGS section at bottom */}
                    <div style={{ marginTop: 10 }}>
                      <label style={{ fontWeight: "bold" }}>Tags:</label>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 8,
                          padding: "6px",
                          border: "1px solid #ccc",
                          borderRadius: 6,
                          minHeight: "40px",
                        }}
                        onClick={() =>
                          document.getElementById("tagInput")?.focus()
                        }
                      >
                        {(newItem.tags || []).map((tag, i) => (
                          <div
                            key={i}
                            style={{
                              background: "#eee",
                              padding: "4px 8px",
                              borderRadius: "20px",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <span style={{ marginRight: 6 }}>{tag}</span>
                            <button
                              onClick={() => {
                                const updated = newItem.tags.filter(
                                  (_, idx) => idx !== i
                                );
                                setNewItem({ ...newItem, tags: updated });
                              }}
                              style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontWeight: "bold",
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}

                        <input
                          id="tagInput"
                          type="text"
                          value={newItem.newTag || ""}
                          onChange={(e) =>
                            setNewItem({ ...newItem, newTag: e.target.value })
                          }
                          onKeyDown={(e) => {
                            if (e.key === "," || e.key === "Enter") {
                              e.preventDefault();
                              const cleaned = (newItem.newTag || "")
                                .trim()
                                .replace(/,+$/, "");
                              if (
                                cleaned &&
                                !newItem.tags.includes(cleaned.toLowerCase())
                              ) {
                                setNewItem({
                                  ...newItem,
                                  tags: [
                                    ...newItem.tags,
                                    cleaned.toLowerCase(),
                                  ],
                                  newTag: "",
                                });
                              }
                            } else if (
                              e.key === "Backspace" &&
                              !newItem.newTag &&
                              newItem.tags.length
                            ) {
                              const tagsCopy = [...newItem.tags];
                              const last = tagsCopy.pop();
                              setNewItem({
                                ...newItem,
                                tags: tagsCopy,
                                newTag: last,
                              });
                            }
                          }}
                          placeholder="Add a tag and press comma or Enter"
                          style={{ border: "none", outline: "none", flex: 1 }}
                        />
                      </div>
                    </div>

                    {/* Buttons */}
                    <div style={{ marginTop: 10 }}>
                      <button
                        onClick={() => {
                          const { newTag, ...cleanItem } = newItem;
                          const updated = [
                            ...profileDraft.inventory,
                            cleanItem,
                          ];
                          handleProfileUpdate("inventory", updated);
                          setProfileDraft((prev) => ({
                            ...prev,
                            inventory: updated,
                          }));
                          setTagFilter("");
                          setNewItem({
                            name: "",
                            quality: "Poor",
                            rarity: "Common",
                            qty: 1,
                            tags: [],
                            description: "",
                            newTag: "",
                          });
                          setShowModal(false);
                        }}
                      >
                        Add
                      </button>
                      <button
                        onClick={() => setShowModal(false)}
                        style={{ marginLeft: 10 }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {view === "crafting" && (isDM || craftingVisible) && (
            <div
              style={{
                maxWidth: 700,
                background: "#f4f4f4",
                padding: "1rem",
                borderRadius: 8,
              }}
            >
              <h3>🛠️ Craft Item</h3>
              <label>
                Item Name:{" "}
                <input
                  value={itemName}
                  onChange={(e) => setItemName(e.target.value)}
                  style={{ width: "100%" }}
                />
              </label>
              <br />
              <br />
              <label>
                Complexity:{" "}
                <select
                  value={complexity}
                  onChange={(e) => setComplexity(e.target.value)}
                >
                  <option>Simple</option>
                  <option>Focused</option>
                  <option>Complex</option>
                  <option>Engineered</option>
                  <option>Unimaginable</option>
                </select>
              </label>
              <br />
              <p>
                Constitution Modifier:{" "}
                {(calculateModifier(profile?.abilityScores?.CON ?? 10) >= 0
                  ? "+"
                  : "") + calculateModifier(profile?.abilityScores?.CON ?? 10)}
              </p>

              <p>
                Intelligence Modifier:{" "}
                {(calculateModifier(profile?.abilityScores?.INT ?? 10) >= 0
                  ? "+"
                  : "") + calculateModifier(profile?.abilityScores?.INT ?? 10)}
              </p>

              <h4>Materials</h4>
              <select
                value=""
                onChange={(e) => {
                  const selected = profile.inventory.find(
                    (i) => i.name === e.target.value
                  );
                  if (selected) {
                    const mat = {
                      name: selected.name,
                      quality: selected.quality,
                      rarity: selected.rarity,
                      qty: 1,
                      maxQty: selected.qty,
                    };
                    setMaterials((prev) => [...prev, mat]);
                  }
                }}
              >
                <option value="">-- Select from Inventory --</option>
                {(profile?.inventory || []).map((item, i) => {
                  const alreadyAdded = materials.some(
                    (m) =>
                      m.name === item.name &&
                      m.quality === item.quality &&
                      m.rarity === item.rarity
                  );
                  return (
                    <option
                      key={i}
                      value={item.name}
                      disabled={materials.some(
                        (m) =>
                          m.name === item.name &&
                          m.quality === item.quality &&
                          m.rarity === item.rarity
                      )}
                    >
                      {item.name} ({item.quality}, {item.rarity}, Qty:{" "}
                      {item.qty})
                      {materials.find(
                        (m) =>
                          m.name === item.name &&
                          m.quality === item.quality &&
                          m.rarity === item.rarity
                      )
                        ? ` — Using: ${
                            materials.find(
                              (m) =>
                                m.name === item.name &&
                                m.quality === item.quality &&
                                m.rarity === item.rarity
                            ).qty
                          }`
                        : ""}
                    </option>
                  );
                })}
              </select>

              {materials.map((m, i) => (
                <li key={i}>
                  {m.name} ({m.quality}, {m.rarity}) — Qty:{" "}
                  <input
                    type="number"
                    min={1}
                    max={m.maxQty}
                    value={m.qty}
                    onChange={(e) => {
                      const updated = [...materials];
                      const newQty = Math.max(
                        1,
                        Math.min(parseInt(e.target.value || "1"), m.maxQty)
                      );
                      updated[i].qty = newQty;
                      setMaterials(updated);
                    }}
                    style={{ width: 50, marginLeft: 5 }}
                  />
                  <button
                    onClick={() => {
                      const updated = [...materials];
                      updated.splice(i, 1);
                      setMaterials(updated);
                    }}
                    style={{ marginLeft: 10 }}
                  >
                    Remove
                  </button>
                </li>
              ))}

              <h4>🧠 Crafting Skill</h4>
              <label>
                <input
                  type="checkbox"
                  checked={hasTool}
                  onChange={(e) => setHasTool(e.target.checked)}
                />{" "}
                I have a suitable crafting tool
              </label>
              {hasTool && (
                <select
                  value={toolQuality}
                  onChange={(e) => setToolQuality(e.target.value)}
                >
                  <option>Poor</option>
                  <option>Good</option>
                  <option>Reliable</option>
                  <option>Finest</option>
                  <option>Impeccable</option>
                </select>
              )}
              <br />
              <button onClick={handleCraft} style={{ marginTop: 12 }}>
                Craft Item
              </button>
              <p style={{ marginTop: 12 }}>{finalResult}</p>
              {log.length > 0 && (
                <pre
                  style={{
                    background: "#f5f5f5",
                    padding: "1rem",
                    marginTop: "1rem",
                    borderRadius: "8px",
                    whiteSpace: "pre-wrap",
                    maxHeight: "300px",
                    overflowY: "auto",
                    fontSize: "0.9rem",
                  }}
                >
                  {log.join("\n")}
                </pre>
              )}
            </div>
          )}

          {view === "dm" && isDM && (
            <>
              <DiscoverMaterialButton
                currentLocation={currentLocation}
                currentEvent={currentEvent}
                adventureLogs={adventureLogs}
              />

              {/* 🔥 New: Add Toggle Button */}
              <button
                onClick={() => setShowMaterials((prev) => !prev)}
                style={{
                  marginTop: 12,
                  marginBottom: 12,
                  padding: "6px 12px",
                  borderRadius: 6,
                  border: "1px solid #999",
                  background: "#eee",
                  cursor: "pointer",
                }}
              >
                {showMaterials
                  ? "Hide Discovered Materials"
                  : "Show Discovered Materials"}
              </button>

              {/* 🔥 New: Conditional render */}
              {showMaterials && <DiscoveredMaterialsViewer />}

              <WorldContextPanel
                currentLocation={currentLocation}
                setCurrentLocation={setCurrentLocation}
                currentEvent={currentEvent}
                setCurrentEvent={setCurrentEvent}
                adventureLogs={adventureLogs}
                setAdventureLogs={setAdventureLogs}
              />

              <div
                style={{
                  maxWidth: 700,
                  padding: "1rem",
                  background: "#f9f9f9",
                  borderRadius: 8,
                }}
              >
                <h3>DM Dashboard</h3>
                {allProfiles.length === 0 && <p>No players found.</p>}

                {allProfiles.map((p) => (
                  <div key={p.id} style={{ marginBottom: "1rem" }}>
                    <button
                      onClick={() => setSelectedProfile(p)}
                      style={{
                        fontSize: "1.1rem",
                        padding: "0.4rem 0.8rem",
                        fontWeight: "bold",
                        cursor: "pointer",
                        border: "1px solid #999",
                        borderRadius: "5px",
                        background: "#eee",
                      }}
                    >
                      {p.name || "Unnamed Player"}
                    </button>
                  </div>
                ))}
                {selectedProfile &&
                  selectedProfile.abilityScores &&
                  selectedProfile.skills && (
                    <div
                      style={{
                        borderTop: "1px solid #ccc",
                        marginTop: "1rem",
                        paddingTop: "1rem",
                      }}
                    >
                      <h4>
                        Editing: {selectedProfile.name || "Unnamed Player"}
                      </h4>

                      <button
                        onClick={() => setSelectedProfile(null)}
                        style={{
                          marginBottom: "1rem",
                          padding: "0.4rem 0.8rem",
                          background: "#ddd",
                          border: "1px solid #aaa",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Close
                      </button>

                      <p>
                        HP:{" "}
                        <input
                          type="number"
                          value={selectedProfile.hp?.current ?? 0}
                          onChange={(e) => {
                            const newHP = {
                              ...selectedProfile.hp,
                              current: parseInt(e.target.value),
                            };
                            updateDoc(doc(db, "profiles", selectedProfile.id), {
                              hp: newHP,
                            });
                          }}
                          style={{ width: "100%" }}
                        />{" "}
                        /{" "}
                        <input
                          type="number"
                          value={selectedProfile.hp?.max ?? 0}
                          onChange={(e) => {
                            const newHP = {
                              ...selectedProfile.hp,
                              max: parseInt(e.target.value),
                            };
                            updateDoc(doc(db, "profiles", selectedProfile.id), {
                              hp: newHP,
                            });
                          }}
                          style={{ width: "100%" }}
                        />
                      </p>

                      <p>
                        Armor Class (AC):{" "}
                        <input
                          type="number"
                          value={selectedProfile.ac ?? 10}
                          onChange={(e) => {
                            const ac = parseInt(e.target.value);
                            updateDoc(doc(db, "profiles", selectedProfile.id), {
                              ac,
                            });
                          }}
                        />
                      </p>

                      <p>
                        Speed (FT):{" "}
                        <input
                          type="number"
                          value={selectedProfile.speed ?? 30}
                          onChange={(e) => {
                            const speed = parseInt(e.target.value);
                            updateDoc(doc(db, "profiles", selectedProfile.id), {
                              speed,
                            });
                          }}
                        />
                      </p>

                      <h3>Condition:</h3>
                      <p>
                        Body Temp (%):{" "}
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={selectedProfile.bodyTemp ?? 50}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            updateDoc(doc(db, "profiles", selectedProfile.id), {
                              bodyTemp: val,
                            });
                          }}
                        />
                      </p>
                      <p>
                        Hunger (%):{" "}
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={selectedProfile.hunger ?? 50}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            updateDoc(doc(db, "profiles", selectedProfile.id), {
                              hunger: val,
                            });
                          }}
                        />
                      </p>
                    </div>
                  )}

                <h3>Ability Scores:</h3>
                {selectedProfile?.abilityScores ? (
                  ABILITY_ORDER.map((key) => {
                    const val = selectedProfile.abilityScores[key] ?? 0;
                    const mod = Math.floor((val - 10) / 2);
                    return (
                      <p key={key}>
                        {key}:{" "}
                        <input
                          type="number"
                          value={val}
                          onChange={(e) => {
                            const score = parseInt(e.target.value);
                            const updated = {
                              ...selectedProfile.abilityScores,
                              [key]: score,
                            };
                            updateDoc(doc(db, "profiles", selectedProfile.id), {
                              abilityScores: updated,
                            });
                            setSelectedProfile((prev) => ({
                              ...prev,
                              abilityScores: updated,
                            }));
                          }}
                          style={{ width: "100%" }}
                        />
                        <span
                          style={{
                            marginLeft: 10,
                            color: "#555",
                            fontSize: "0.85rem",
                          }}
                        >
                          (Mod: {mod >= 0 ? "+" : ""}
                          {mod})
                        </span>
                      </p>
                    );
                  })
                ) : (
                  <p>Loading ability scores...</p>
                )}

                <h3>Skills:</h3>
                {selectedProfile?.skills ? (
                  SKILL_ORDER.map((skill) => {
                    const value = selectedProfile.skills[skill] ?? 0;
                    return (
                      <div key={skill}>
                        {skill}:{" "}
                        {[1, 2, 3, 4, 5].map((rank) => (
                          <input
                            key={rank}
                            type="checkbox"
                            checked={value >= rank}
                            onChange={() => {
                              const newRank = rank === value ? rank - 1 : rank;
                              const updated = {
                                ...selectedProfile.skills,
                                [skill]: newRank,
                              };
                              updateDoc(
                                doc(db, "profiles", selectedProfile.id),
                                {
                                  skills: updated,
                                }
                              );
                              setSelectedProfile((prev) => ({
                                ...prev,
                                skills: updated,
                              }));
                            }}
                            style={{ marginRight: 6 }}
                          />
                        ))}
                      </div>
                    );
                  })
                ) : (
                  <p>Loading skills...</p>
                )}

                <h3>Inventory:</h3>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {selectedProfile?.inventory?.length > 0 && (
                    <div style={{ marginBottom: "8px" }}>
                      <select
                        value={tagFilter}
                        onChange={(e) => setTagFilter(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "6px",
                          borderRadius: "6px",
                          border: "1px solid #ccc",
                        }}
                      >
                        <option value="">Show All Items</option>
                        {[
                          ...new Set(
                            selectedProfile.inventory.flatMap(
                              (item) => item.tags || []
                            )
                          ),
                        ]
                          .filter((tag) => tag) // remove empty tags
                          .sort()
                          .map((tag, idx) => (
                            <option key={idx} value={tag}>
                              {tag}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}

                  {(selectedProfile?.inventory || [])
                    .filter((item) =>
                      !tagFilter.trim()
                        ? true
                        : (item.tags || []).includes(
                            tagFilter.trim().toLowerCase()
                          )
                    )
                    .map((item, index) => (
                      <div
                        key={index}
                        onClick={() => {
                          setSelectedItem({ ...item, index });
                          setShowItemModal(true);
                        }}
                        style={{
                          padding: "10px",
                          background: "#f9f9f9",
                          borderRadius: "6px",
                          cursor: "pointer",
                          border: "1px solid #ccc",
                        }}
                      >
                        <strong>{item.name}</strong> — {item.quality},{" "}
                        {item.rarity} — Qty: {item.qty}
                        <div style={{ fontSize: "0.85rem", marginTop: 4 }}>
                          Tags: {item.tags?.join(", ") || "None"}
                        </div>
                      </div>
                    ))}
                </div>

                <button
                  onClick={() => setShowDMModal(true)}
                  style={{ marginTop: 10 }}
                >
                  ➕ Add New Item
                </button>

                {showDMModal && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      background: "rgba(0,0,0,0.5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1000,
                    }}
                  >
                    <div
                      style={{
                        background: "white",
                        padding: 20,
                        borderRadius: 8,
                        width: 300,
                      }}
                    >
                      <h3>Add Item to {selectedProfile.name}</h3>

                      <input
                        value={dmNewItem.name}
                        onChange={(e) =>
                          setDMNewItem({ ...dmNewItem, name: e.target.value })
                        }
                        placeholder="Item name"
                        style={{ width: "100%", marginBottom: 8 }}
                      />

                      <select
                        value={dmNewItem.quality}
                        onChange={(e) =>
                          setDMNewItem({
                            ...dmNewItem,
                            quality: e.target.value,
                          })
                        }
                        style={{ width: "100%", marginBottom: 8 }}
                      >
                        {QUALITIES.map((q) => (
                          <option key={q}>{q}</option>
                        ))}
                      </select>

                      <select
                        value={dmNewItem.rarity}
                        onChange={(e) =>
                          setDMNewItem({ ...dmNewItem, rarity: e.target.value })
                        }
                        style={{ width: "100%", marginBottom: 8 }}
                      >
                        {RARITIES.map((r) => (
                          <option key={r}>{r}</option>
                        ))}
                      </select>

                      <input
                        type="number"
                        min={1}
                        value={dmNewItem.qty}
                        onChange={(e) =>
                          setDMNewItem({
                            ...dmNewItem,
                            qty: parseInt(e.target.value),
                          })
                        }
                        style={{ width: "100%", marginBottom: 10 }}
                      />
                      <input
                        value={dmNewItem.description}
                        onChange={(e) =>
                          setDMNewItem({
                            ...dmNewItem,
                            description: e.target.value,
                          })
                        }
                        placeholder="Description"
                        style={{ width: "100%", marginBottom: 8 }}
                      />
                      <div style={{ marginTop: 10 }}>
                        <label style={{ fontWeight: "bold" }}>Tags:</label>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 8,
                            padding: "6px",
                            border: "1px solid #ccc",
                            borderRadius: 6,
                            minHeight: "40px",
                          }}
                          onClick={() =>
                            document.getElementById("dmTagInput")?.focus()
                          }
                        >
                          {(dmNewItem.tags || []).map((tag, i) => (
                            <div
                              key={i}
                              style={{
                                background: "#eee",
                                padding: "4px 8px",
                                borderRadius: "20px",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <span style={{ marginRight: 6 }}>{tag}</span>
                              <button
                                onClick={() => {
                                  const updatedTags = dmNewItem.tags.filter(
                                    (_, idx) => idx !== i
                                  );
                                  setDMNewItem({
                                    ...dmNewItem,
                                    tags: updatedTags,
                                  });
                                }}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  cursor: "pointer",
                                  fontWeight: "bold",
                                }}
                              >
                                ×
                              </button>
                            </div>
                          ))}

                          <input
                            id="dmTagInput"
                            type="text"
                            value={dmNewItem.newTag || ""}
                            onChange={(e) =>
                              setDMNewItem({
                                ...dmNewItem,
                                newTag: e.target.value,
                              })
                            }
                            onKeyDown={(e) => {
                              if (e.key === "," || e.key === "Enter") {
                                e.preventDefault();
                                const cleaned = (dmNewItem.newTag || "")
                                  .trim()
                                  .replace(/,+$/, "");
                                if (
                                  cleaned &&
                                  !(dmNewItem.tags || []).includes(
                                    cleaned.toLowerCase()
                                  )
                                ) {
                                  setDMNewItem({
                                    ...dmNewItem,
                                    tags: [
                                      ...(dmNewItem.tags || []),
                                      cleaned.toLowerCase(),
                                    ],
                                    newTag: "",
                                  });
                                }
                              }
                            }}
                            placeholder="Add tag, press comma or Enter"
                            style={{ border: "none", outline: "none", flex: 1 }}
                          />
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <button
                          onClick={() => {
                            const updated = [
                              ...(selectedProfile.inventory || []),
                              { ...dmNewItem },
                            ];
                            updateDoc(doc(db, "profiles", selectedProfile.id), {
                              inventory: updated,
                            });
                            setSelectedProfile((prev) => ({
                              ...prev,
                              inventory: updated,
                            }));
                            setDMNewItem({
                              name: "",
                              quality: "Poor",
                              rarity: "Common",
                              qty: 1,
                              tags: [],
                              description: "",
                            });
                            setShowDMModal(false);
                          }}
                        >
                          Add
                        </button>
                        <button onClick={() => setShowDMModal(false)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
      {showItemModal && selectedItem && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              padding: 20,
              borderRadius: 12,
              width: "90%",
              maxWidth: 450,
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 0 20px rgba(0,0,0,0.2)",
            }}
          >
            <h2 style={{ marginBottom: 20 }}>Edit Item</h2>

            {/* Name */}
            <input
              value={selectedItem.name}
              onChange={(e) =>
                setSelectedItem({ ...selectedItem, name: e.target.value })
              }
              placeholder="Item Name"
              style={{ width: "100%", marginBottom: 12 }}
            />

            {/* Quality */}
            <select
              value={selectedItem.quality}
              onChange={(e) =>
                setSelectedItem({ ...selectedItem, quality: e.target.value })
              }
              style={{ width: "100%", marginBottom: 12 }}
            >
              {QUALITIES.map((q) => (
                <option key={q}>{q}</option>
              ))}
            </select>

            {/* Rarity */}
            <select
              value={selectedItem.rarity}
              onChange={(e) =>
                setSelectedItem({ ...selectedItem, rarity: e.target.value })
              }
              style={{ width: "100%", marginBottom: 12 }}
            >
              {RARITIES.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>

            {/* Quantity */}
            <input
              type="number"
              min={1}
              value={selectedItem.qty}
              onChange={(e) =>
                setSelectedItem({
                  ...selectedItem,
                  qty: parseInt(e.target.value),
                })
              }
              style={{ width: "100%", marginBottom: 12 }}
            />

            {/* Description */}
            <textarea
              value={selectedItem.description || ""}
              onChange={(e) =>
                setSelectedItem({
                  ...selectedItem,
                  description: e.target.value,
                })
              }
              placeholder="Item Description"
              rows={3}
              style={{ width: "100%", marginBottom: 12 }}
            />

            {/* Tags */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontWeight: "bold" }}>Tags:</label>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  padding: "6px",
                  border: "1px solid #ccc",
                  borderRadius: 6,
                  minHeight: "40px",
                }}
                onClick={() => document.getElementById("itemTagInput")?.focus()}
              >
                {(selectedItem.tags || []).map((tag, i) => (
                  <div
                    key={i}
                    style={{
                      background: "#eee",
                      padding: "4px 8px",
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ marginRight: 6 }}>{tag}</span>
                    <button
                      onClick={() => {
                        const updatedTags = (selectedItem.tags || []).filter(
                          (_, idx) => idx !== i
                        );
                        setSelectedItem({
                          ...selectedItem,
                          tags: updatedTags,
                        });
                      }}
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        fontWeight: "bold",
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}

                <input
                  id="itemTagInput"
                  type="text"
                  value={selectedItem.newTag || ""}
                  onChange={(e) =>
                    setSelectedItem({
                      ...selectedItem,
                      newTag: e.target.value,
                    })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "," || e.key === "Enter") {
                      e.preventDefault();
                      const cleaned = (selectedItem.newTag || "")
                        .trim()
                        .replace(/,+$/, "");
                      if (
                        cleaned &&
                        !(selectedItem.tags || []).includes(
                          cleaned.toLowerCase()
                        )
                      ) {
                        setSelectedItem({
                          ...selectedItem,
                          tags: [
                            ...(selectedItem.tags || []),
                            cleaned.toLowerCase(),
                          ],
                          newTag: "",
                        });
                      }
                    }
                  }}
                  placeholder="Add tag, press comma or Enter"
                  style={{ border: "none", outline: "none", flex: 1 }}
                />
              </div>
            </div>

            {/* Save / Cancel / Delete / Duplicate buttons */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                justifyContent: "space-between",
              }}
            >
              <button
                className="modal-button"
                onClick={() => {
                  const updatedInventory =
                    view === "dm"
                      ? [...selectedProfile.inventory]
                      : [...profileDraft.inventory];

                  const cleanedItem = {
                    ...selectedItem,
                    tags: (selectedItem.tags || []).map((tag) =>
                      tag.trim().toLowerCase()
                    ),
                  };
                  delete cleanedItem.newTag;

                  const realIndex = updatedInventory.findIndex(
                    (item) =>
                      item.name === selectedItem.name &&
                      item.quality === selectedItem.quality &&
                      item.rarity === selectedItem.rarity &&
                      item.qty === selectedItem.qty &&
                      (item.description || "") ===
                        (selectedItem.description || "")
                  );

                  if (realIndex !== -1) {
                    updatedInventory[realIndex] = cleanedItem;

                    if (view === "dm") {
                      debounceDMUpdate(updatedInventory);
                      setSelectedProfile((prev) => ({
                        ...prev,
                        inventory: updatedInventory,
                      }));
                    } else {
                      handleProfileUpdate("inventory", updatedInventory);
                      setProfileDraft((prev) => ({
                        ...prev,
                        inventory: updatedInventory,
                      }));
                    }
                  }
                  const hasMatch = updatedInventory.some((item) =>
                    (item.tags || []).includes(tagFilter.trim().toLowerCase())
                  );
                  if (!hasMatch && tagFilter.trim()) {
                    setTagFilter(""); // Reset to Show All immediately
                  }

                  setShowItemModal(false);
                  setSelectedItem(null);

                  // Fix tagFilter after save
                }}
              >
                Save
              </button>

              <button
                className="modal-button cancel"
                onClick={() => {
                  setShowItemModal(false);
                  setSelectedItem(null);
                }}
              >
                Cancel
              </button>

              <button
                className="modal-button delete"
                onClick={() => {
                  const updatedInventory =
                    view === "dm"
                      ? [...selectedProfile.inventory]
                      : [...profileDraft.inventory];

                  const realIndex = updatedInventory.findIndex(
                    (item) =>
                      item.name === selectedItem.name &&
                      item.quality === selectedItem.quality &&
                      item.rarity === selectedItem.rarity &&
                      item.qty === selectedItem.qty &&
                      (item.description || "") ===
                        (selectedItem.description || "")
                  );

                  if (realIndex !== -1) {
                    updatedInventory.splice(realIndex, 1);

                    if (view === "dm") {
                      debounceDMUpdate(updatedInventory);
                      setSelectedProfile((prev) => ({
                        ...prev,
                        inventory: updatedInventory,
                      }));
                    } else {
                      handleProfileUpdate("inventory", updatedInventory);
                      setProfileDraft((prev) => ({
                        ...prev,
                        inventory: updatedInventory,
                      }));
                    }
                  }
                  const hasMatch = updatedInventory.some((item) =>
                    (item.tags || []).includes(tagFilter.trim().toLowerCase())
                  );
                  if (!hasMatch && tagFilter.trim()) {
                    setTagFilter(""); // Reset to Show All immediately
                  }
                  setShowItemModal(false);
                  setSelectedItem(null);
                }}
              >
                Delete
              </button>

              <button
                className="modal-button duplicate"
                onClick={() => {
                  const updatedInventory =
                    view === "dm"
                      ? [...selectedProfile.inventory]
                      : [...profileDraft.inventory];
                  updatedInventory.push({
                    ...selectedItem,
                    name: selectedItem.name + " (Copy)",
                  });

                  if (view === "dm") {
                    debounceDMUpdate(updatedInventory);
                    setSelectedProfile((prev) => ({
                      ...prev,
                      inventory: updatedInventory,
                    }));
                  } else {
                    handleProfileUpdate("inventory", updatedInventory);
                    setProfileDraft((prev) => ({
                      ...prev,
                      inventory: updatedInventory,
                    }));
                  }

                  setShowItemModal(false);
                  setSelectedItem(null);
                }}
              >
                Duplicate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
